import React, { useState, useRef, useEffect } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { useSalesColumns } from './hooks/useSalesColumns';
import { SalesFormFields } from '@src/components/shared/forms';
import { PaymentFormModal } from '@src/pages/payments/components';
import { useInvalidate } from '@refinedev/core';
import { PrinterOutlined, CopyOutlined } from '@ant-design/icons';
import { SalesPrintComponent } from './components';
import { message } from 'antd';

interface PrintRef {
  print: () => void;
}

export const SalesPage = () => {
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentInitialValues, setPaymentInitialValues] = useState<any>({});
  const [selectedRecordForPrint, setSelectedRecordForPrint] =
    useState<any>(null);
  const [copySaleData, setCopySaleData] = useState<any>(null);
  const [autoOpenCreate, setAutoOpenCreate] = useState(false);
  const [copyId, setCopyId] = useState<number>(0);
  const [formInstanceKey, setFormInstanceKey] = useState<string>('');
  const printRef = useRef<PrintRef>(null);
  const invalidate = useInvalidate();

  // Check for prescription data when component mounts
  useEffect(() => {
    const prescriptionData = localStorage.getItem('prescriptionToSaleData');

    if (prescriptionData) {
      try {
        const saleData = JSON.parse(prescriptionData);

        // Format the data properly for the sales form
        const formattedSaleData = {
          ...saleData,
          // Mark this as coming from prescription
          fromPrescription: true,
          // Ensure patient_id is properly set
          patient_id: saleData.patient_id,
          // Format sale items to work with the inventory selection
          sale_items: Array.isArray(saleData.sale_items)
            ? saleData.sale_items.map((item: any) => {
                // Ensure proper structure for prescription data
                return {
                  ...item,
                  // Mark this item as coming from prescription
                  fromPrescription: true,
                  // Ensure proper IDs are set
                  inventory_id: item.inventory_id || item.medicine_id,
                  medicine_id: item.medicine_id,
                  // Ensure medicine object has all required fields including inventory
                  medicine: item.medicine
                    ? {
                        ...item.medicine,
                        id: item.medicine.id,
                      }
                    : null,
                  _action: 'create' as const,
                  // Ensure all calculated fields are present
                  amount: item.amount || 0,
                  discount_amount: item.discount_amount || 0,
                  discounted_base_price:
                    item.discounted_base_price || item.amount || 0,
                  gst_amount: item.gst_amount || 0,
                  net_amount: item.net_amount || 0,
                };
              })
            : [],
          // Ensure financial totals are properly set (including total_rate for SalesFormFields)
          total_rate: saleData.total_rate ?? saleData.subtotal ?? 0,
          subtotal: saleData.subtotal || 0,
          tax_amount: saleData.tax_amount || 0,
          total_amount: saleData.total_amount || 0,
          bill_amount: saleData.bill_amount || saleData.total_amount || 0,
          due_amount: saleData.due_amount || saleData.total_amount || 0,
          overall_discount_rate: saleData.overall_discount_rate || 0,
          overall_discount_amount: saleData.overall_discount_amount || 0,
          clinic_due_amount: saleData.clinic_due_amount || 0,
          payment_status: saleData.payment_status || 'UNPAID',
        };

        setCopySaleData(formattedSaleData);
        setAutoOpenCreate(true);

        // Clear the localStorage data
        localStorage.removeItem('prescriptionToSaleData');

        message.success('Sale form opened with prescription data');
      } catch (error) {
        console.error('Error parsing prescription data:', error);
        localStorage.removeItem('prescriptionToSaleData');
      }
    }
  }, []);

  const handleCreatePayment = (transactionData: {
    transaction_type: 'sell';
    s_id: number;
    amount: number;
    customer_name: string;
    type: string;
  }) => {
    setPaymentInitialValues({
      type: transactionData.type || 'MEDICAL_SALES',
      reference_id: transactionData.s_id,
      transaction_type: 'CREDIT',
      amount: transactionData.amount,
    });
    setPaymentModalVisible(true);
  };

  const handlePaymentSuccess = () => {
    // Invalidate sales to refresh the data
    invalidate({
      resource: 'sales',
      invalidates: ['list'],
    });
    invalidate({
      resource: 'payments',
      invalidates: ['list'],
    });
    setPaymentModalVisible(false);
    setPaymentInitialValues({});
  };

  const onPrintClick = (record: any) => {
    setSelectedRecordForPrint(record);
    setTimeout(() => {
      printRef.current?.print();
    }, 100);
  };

  const onCopyClick = (record: any) => {
    if (!record) {
      message.error('No record selected for copying');
      return;
    }

    if (copySaleData) {
      message.warning('Please complete the current copy operation first');
      return;
    }

    try {
      const newCopyId = copyId + 1;
      const timestamp = Date.now();
      const uniqueFormKey = `form_${timestamp}_${newCopyId}_${Math.random().toString(36).substr(2, 9)}`;

      setCopyId(newCopyId);
      setFormInstanceKey(uniqueFormKey);

      // Prepare copy data
      const copyData = {
        copyId: newCopyId,
        timestamp: timestamp,
        formInstanceKey: uniqueFormKey,
        patient_id: record.patient_id || null,
        customer_name: record.customer_name || '',
        customer_phone: record.customer_phone || '',
        date: record.date || new Date().toISOString(),
        notes: record.notes || '',
        sale_items: Array.isArray(record.sale_items)
          ? record.sale_items.map((item: any) => ({
              medicine_id: item.medicine_id || item.inventory_id || '',
              inventory_id: item.inventory_id || item.medicine_id || '',
              medicine: item.medicine || null,
              medicine_name: item.medicine_name || item.name || '',
              mrp: item.mrp || 0,
              quantity: item.quantity || 1,
              discount: item.discount || 0,
              gst_rate: item.gst_rate || 0,
              amount: item.amount || 0,
              discount_amount: item.discount_amount || 0,
              discounted_base_price: item.discounted_base_price || 0,
              gst_amount: item.gst_amount || 0,
              net_amount: item.net_amount || 0,
            }))
          : [],
        total_rate: record.total_rate ?? record.subtotal ?? 0,
        subtotal: record.subtotal || 0,
        discount: record.discount || 0,
        tax_amount: record.tax_amount || 0,
        total_amount: record.total_amount || 0,
        overall_discount_rate: record.overall_discount_rate || 0,
        overall_discount_amount: record.overall_discount_amount || 0,
        bill_amount: record.bill_amount || 0,
        due_amount: record.due_amount || 0,
        clinic_due_amount: record.clinic_due_amount || 0,
        payment_status: record.payment_status || 'UNPAID',
      };

      setCopySaleData(copyData);
      setAutoOpenCreate(true);
      message.success('Sale copied successfully!');
    } catch (error) {
      console.error('Error copying sale:', error);
      message.error('Failed to copy sale. Please try again.');
    }
  };

  const handleCreateSuccess = () => {
    // Reset copy state
    setCopySaleData(null);
    setAutoOpenCreate(false);
    setCopyId(0);
    setFormInstanceKey('');

    // Invalidate sales to refresh the data
    invalidate({
      resource: 'sales',
      invalidates: ['list'],
    });
  };

  const handleCreateDrawerClose = () => {
    // Reset copy state
    setCopySaleData(null);
    setAutoOpenCreate(false);
    setCopyId(0);
    setFormInstanceKey('');
  };

  const columns = useSalesColumns({
    onCreateTransaction: handleCreatePayment,
  });
  const formFields = SalesFormFields();

  // Custom actions for the table
  const customActions = [
    // {
    //   key: 'copy',
    //   icon: <CopyOutlined />,
    //   label: '',
    //   onClick: onCopyClick,
    //   type: 'default' as const,
    //   tooltip: 'Copy Sale',
    // },
    {
      key: 'print',
      icon: <PrinterOutlined />,
      label: '',
      onClick: onPrintClick,
      type: 'default' as const,
      tooltip: 'Print Sale',
    },
  ];

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SmartDataTable
        key={formInstanceKey || `sales-table-${copyId}`}
        api={{
          resource: 'sales',
        }}
        title="Sales"
        columns={columns}
        search={{
          enabled: true,
          fields: ['id', 'customer_name', 'customer_phone'],
          placeholder: 'Search by ID, customer name, or phone...',
          debounceMs: 500,
        }}
        createForm={{
          fields: formFields(copySaleData) as any,
          title: 'Create Sale',
          submitText: 'Create Sale',
          initialValues: copySaleData
            ? {
                ...copySaleData,
                _copyTimestamp: copySaleData.timestamp,
                _copyId: copySaleData.copyId,
                _formInstanceKey: copySaleData.formInstanceKey,
              }
            : undefined,
        }}
        dynamicEditForm={{
          getForm: record => ({
            fields: formFields(record) as any,
            title: 'Edit Sale',
            submitText: 'Save Changes',
          }),
          getResource: () => 'sales',
        }}
        formDisplayMode="drawer"
        drawerWidth={1200}
        modalWidth={1000}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={false}
        pageSize={10}
        pageOptions={[10, 20, 50, 100]}
        showSizeChanger={true}
        showQuickJumper={false}
        // showExport={true}
        showColumnVisibility={true}
        autoOpenCreate={autoOpenCreate}
        onCreateSuccess={handleCreateSuccess}
        onCreateDrawerClose={handleCreateDrawerClose}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 300px)',
          },
        }}
        rowKey="id"
        isFilter={true}
        customActions={customActions}
      />

      {/* Print Component */}
      {selectedRecordForPrint && (
        <div className="print-content" style={{ display: 'none' }}>
          <SalesPrintComponent ref={printRef} record={selectedRecordForPrint} />
        </div>
      )}

      <PaymentFormModal
        visible={paymentModalVisible}
        onClose={() => {
          setPaymentModalVisible(false);
          setPaymentInitialValues({});
        }}
        onSuccess={handlePaymentSuccess}
        customHeader={{
          message: 'Please take google review from patient',
          type: 'info',
          showIcon: true,
        }}
        initialValues={paymentInitialValues}
      />
    </div>
  );
};
